import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import ModalLayout from '../layouts/ModalLayout';
import Input from '../misc/Input';
import Labeled from '../misc/Labeled';
import Button from '../buttons/Button';
import * as bankAccountService from '../../../services/community/bankAccountService';
import {
  CreateFinancialCategoryDto,
  FinancialCategory,
} from '../../../types/bankAccount';

export const COLOR_OPTIONS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#6366F1',
  '#14B8A6',
  '#6B7280',
  '#0EA5E9',
];

interface CreateCategoryModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onCreated: (category: FinancialCategory) => void;
  onClose?: () => void;
  initialName?: string;
  initialColor?: string;
}

const extractErrorMessage = (error: unknown): string => {
  return (
    (error as { response?: { data?: { message?: string } }; message?: string })
      ?.response?.data?.message ??
    (error as Error)?.message ??
    String(error)
  );
};

export default function CreateCategoryModal({
  open,
  setOpen,
  onCreated,
  onClose,
  initialName = '',
  initialColor = COLOR_OPTIONS[0],
}: CreateCategoryModalProps): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.categories' });
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(COLOR_OPTIONS[0]);
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    setName(initialName);
    setDescription('');
    setColor(initialColor);
  }, [open, initialName, initialColor]);

  const reset = () => {
    setName('');
    setDescription('');
    setColor(COLOR_OPTIONS[0]);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error(t('errors.nameRequired'));
      return;
    }
    try {
      setSubmitting(true);
      const dto: CreateFinancialCategoryDto = {
        name: name.trim(),
        description: description.trim() || null,
        color,
      };
      const created = await bankAccountService.createCategory(dto);
      toast.success(t('success.created'));
      onCreated(created);
      reset();
      setOpen(false);
    } catch (error: unknown) {
      toast.error(extractErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      reset();
      onClose?.();
    }
    setOpen(next);
  };

  return (
    <ModalLayout
      open={open}
      setOpen={handleOpenChange}
      closeButton
      className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[500px]">
      <div>
        <h2 className="mb-4 text-lg font-semibold">{t('createModal.title')}</h2>
        <div className="space-y-4">
          <Input
            id="create-category-name"
            label={t('createModal.name')}
            type="text"
            placeholder={t('createModal.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <Labeled label={t('createModal.description')}>
            <textarea
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder={t('createModal.descriptionPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Labeled>
          <Labeled label={t('createModal.color')}>
            <div className="grid grid-cols-5 gap-2">
              {COLOR_OPTIONS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={c}
                  className={`h-8 w-8 rounded-full border-2 ${
                    color === c ? 'border-gray-900' : 'border-gray-300'
                  }`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </Labeled>
          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="secondary" onClick={() => handleOpenChange(false)}>
              {t('cancel')}
            </Button>
            <Button variant="primary-dark" onClick={handleSubmit} disabled={submitting}>
              {submitting ? t('creating') ?? '…' : t('create')}
            </Button>
          </div>
        </div>
      </div>
    </ModalLayout>
  );
}